import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { validators } from 'investira.sdk';
import {
  Stack,
  LoadingButton,
  Icon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography
} from 'investira.react.components';
import { PageTemplate } from '../../../components/templates';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import services from '../../../services';
import utils from '../../../utils';
import { AVALIACAO_STATUS } from '../../../enums';
import AvalicoesSaveStatus from '../_components/AvalicoesSaveStatus';
import SessaoNavProgress from '../_components/SessaoNavProgress';
import AvaliacoesEditSugestoes from './AvaliacoesEditSugestoes';
import AvaliacoesEditQuestoes from './AvaliacoesEditQuestoes';
import AvaliacoesProgresso from '../_components/AvaliacoesProgresso';
import hydrateAvaliacaoQuestoes from '../_shared/hydrateAvaliacaoQuestoes';
import hydrateAvaliacaoRespostas from '../_shared/hydrateAvaliacaoRespostas';
import normalizarRespostaPayloadParaUpsert from '../_shared/avaliacaoRespostaPayload';
import { getAvaliacaoStatusId } from '../_shared/avaliacaoStatus';
import AvaliacoesGestorEditStatusView from '../AvaliacoesGestorEdit/AvaliacoesGestorEditStatusView';
import withResponseHandling from '../../../hoc/withResponseHandling';

const AvaliacoesEdit = memo(props => {
  const { state, actions } = useAvaliacoesContext();
  const xUsuarioId = useSelector(store => store.user.usuario_id);
  const { sessoes, activeCategoryId, activeQuestionId, activeAliasQuestionId } = state;
  const [isProcessarDialogOpen, setIsProcessarDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [xAvaliacao, setXAvaliacao] = useState(null);
  const [xAvaliacaoSalvando, setXAvaliacaoSalvando] = useState(false);
  const [xModeloQuestoes, setXModeloQuestoes] = useState([]);
  const [xRespostasPorQuestao, setXRespostasPorQuestao] = useState({});
  const [xRevisoesPorQuestao, setXRevisoesPorQuestao] = useState({});
  const [xUploadsAvaliacao, setXUploadsAvaliacao] = useState([]);
  const [xTelaStatus, setXTelaStatus] = useState(null);
  const [mostrarApenasSemVisto, setMostrarApenasSemVisto] = useState(false);
  const xDebouncedUpsertByQuestaoRef = useRef({});
  const xRevisoesPorQuestaoRef = useRef({});
  const xUpsertsPendentesRef = useRef(0);
  const xHydratacao = useMemo(
    () => hydrateAvaliacaoQuestoes(xAvaliacao, xModeloQuestoes),
    [xAvaliacao, xModeloQuestoes]
  );
  const xRespostasHydratadas = useMemo(
    () => hydrateAvaliacaoRespostas(Object.values(xRespostasPorQuestao || {})),
    [xRespostasPorQuestao]
  );

  const navigate = useNavigate();
  const { id } = useParams();

  const pvAtualizarEstadoSalvamento = useCallback(() => {
    setXAvaliacaoSalvando(xUpsertsPendentesRef.current > 0);
  }, []);

  const pvIniciarSalvamentoResposta = useCallback(() => {
    xUpsertsPendentesRef.current += 1;
    pvAtualizarEstadoSalvamento();
  }, [pvAtualizarEstadoSalvamento]);

  const pvFinalizarSalvamentoResposta = useCallback(() => {
    xUpsertsPendentesRef.current = Math.max(0, xUpsertsPendentesRef.current - 1);
    pvAtualizarEstadoSalvamento();
  }, [pvAtualizarEstadoSalvamento]);

  const pvAtualizarDataUltimaAtualizacao = useCallback(pData => {
    const xNovaDataAtualizacao = pData?.modified || pData?.updated_at || new Date().toISOString();

    setXAvaliacao(pPrev =>
      pPrev
        ? {
            ...pPrev,
            modified: xNovaDataAtualizacao,
            updated_at: xNovaDataAtualizacao
          }
        : pPrev
    );
  }, []);

  const pvLimparDadosRevisao = useCallback(() => {
    setXModeloQuestoes([]);
    setXRespostasPorQuestao({});
    setXRevisoesPorQuestao({});
    xRevisoesPorQuestaoRef.current = {};
    setXUploadsAvaliacao([]);
  }, []);

  const pvExtrairRevisoesPorQuestao = useCallback(pRespostasPorQuestao => {
    return Object.entries(pRespostasPorQuestao || {}).reduce((pAcc, [pQuestaoId, pResposta]) => {
      pAcc[pQuestaoId] = {
        revisor_justificativa: pResposta?.revisor_justificativa || '',
        revisor_nota: pResposta?.revisor_nota ?? null,
        revisor_usuario_id: pResposta?.revisor_usuario_id ?? null
      };

      return pAcc;
    }, {});
  }, []);

  const pvUpsertRevisaoDebounced = useCallback(
    (pQuestionId, pPayload) => {
      if (!xDebouncedUpsertByQuestaoRef.current[pQuestionId]) {
        xDebouncedUpsertByQuestaoRef.current[pQuestionId] = utils.helpers.debounce(pData => {
          pvIniciarSalvamentoResposta();
          services.respostas.upsert(
            normalizarRespostaPayloadParaUpsert(pData),
            rRes => {
              pvAtualizarDataUltimaAtualizacao(rRes?.data);
            },
            () => {},
            () => {
              pvFinalizarSalvamentoResposta();
            }
          );
        }, 500);
      }

      xDebouncedUpsertByQuestaoRef.current[pQuestionId](pPayload);
    },
    [pvAtualizarDataUltimaAtualizacao, pvFinalizarSalvamentoResposta, pvIniciarSalvamentoResposta]
  );

  const xQuestoesDisponiveis = useMemo(() => {
    return xHydratacao.questoes;
  }, [xHydratacao]);

  const xQuestoesDisponiveisIds = useMemo(
    () => new Set(xQuestoesDisponiveis.map(xQuestao => xQuestao.id)),
    [xQuestoesDisponiveis]
  );

  const xQuestoesPorSessao = useMemo(() => {
    return xHydratacao.sessoesQuestoes;
  }, [xHydratacao]);

  const xSessoesDisponiveis = useMemo(() => {
    const xSessoesHydratadas = xHydratacao.sessoes;

    if (Object.keys(xSessoesHydratadas).length > 0) {
      return xSessoesHydratadas;
    }

    return sessoes;
  }, [sessoes, xHydratacao]);

  const xSessoesDisponiveisIds = useMemo(
    () => new Set(Object.keys(xSessoesDisponiveis)),
    [xSessoesDisponiveis]
  );
  const xSessoesQuestoesAtivas = useMemo(
    () => xQuestoesPorSessao[activeCategoryId] || { questoes: [] },
    [activeCategoryId, xQuestoesPorSessao]
  );
  const xQuestoesSessaoAtiva = xSessoesQuestoesAtivas.questoes || [];
  const xQuestoesSize = xQuestoesDisponiveis.length;
  const xQuestoesIds = xQuestoesDisponiveisIds;
  const xQuestoesRespondiveis = useMemo(
    () => xQuestoesDisponiveis.filter(pQuestao => !pQuestao.interna),
    [xQuestoesDisponiveis]
  );
  const xQuestoesRespondiveisSize = xQuestoesRespondiveis.length;
  const xRespondidas = useMemo(() => {
    return xQuestoesRespondiveis.reduce((pCount, pQuestao) => {
      return xRespostasHydratadas.respostasPorQuestao?.[pQuestao.id]?.respondida
        ? pCount + 1
        : pCount;
    }, 0);
  }, [xQuestoesRespondiveis, xRespostasHydratadas.respostasPorQuestao]);
  const xQuestaoAtiva = useMemo(() => {
    return (
      xHydratacao.questoesById?.[activeQuestionId] ||
      xQuestoesSessaoAtiva.find(xQuestao => xQuestao.id === activeQuestionId) ||
      null
    );
  }, [activeQuestionId, xHydratacao.questoesById, xQuestoesSessaoAtiva]);
  const xRespostaQuestaoAtiva = xRespostasPorQuestao?.[activeQuestionId] || null;
  const pvIsQuestaoRevisada = useCallback(
    (pQuestionId, pResposta = {}) => {
      const xQuestao = xHydratacao.questoesById?.[Number(pQuestionId)] || null;

      if (xQuestao?.interna) {
        return pResposta?.revisor_usuario_id != null && pResposta?.revisor_nota != null;
      }

      return pResposta?.revisor_usuario_id != null;
    },
    [xHydratacao.questoesById]
  );
  const xQuestoesSessaoExibidas = useMemo(() => {
    if (!mostrarApenasSemVisto) {
      return xQuestoesSessaoAtiva;
    }

    return xQuestoesSessaoAtiva.filter(
      pQuestao => !pvIsQuestaoRevisada(pQuestao.id, xRevisoesPorQuestao?.[pQuestao.id])
    );
  }, [mostrarApenasSemVisto, pvIsQuestaoRevisada, xQuestoesSessaoAtiva, xRevisoesPorQuestao]);

  const xRevisadas = useMemo(() => {
    return Object.entries(xRevisoesPorQuestao || {}).reduce((pCount, [pQuestionId, pResposta]) => {
      if (!xQuestoesIds.has(Number(pQuestionId))) return pCount;
      return pvIsQuestaoRevisada(pQuestionId, pResposta) ? pCount + 1 : pCount;
    }, 0);
  }, [pvIsQuestaoRevisada, xQuestoesIds, xRevisoesPorQuestao]);

  const xSessoesProgress = useMemo(() => {
    return Object.fromEntries(
      Object.values(xSessoesDisponiveis).map(xSessao => {
        const xQuestoesSessao = xQuestoesPorSessao[xSessao.id]?.questoes || [];
        const xTotalSessao = xQuestoesSessao.length;
        const xVistasSessao = xQuestoesSessao.reduce((pCount, pQuestao) => {
          return pvIsQuestaoRevisada(pQuestao.id, xRevisoesPorQuestao?.[pQuestao.id])
            ? pCount + 1
            : pCount;
        }, 0);

        const xProgression = xTotalSessao ? (xVistasSessao / xTotalSessao) * 100 : 0;

        return [
          xSessao.id,
          {
            ...xSessao,
            progression: xProgression,
            completed: xTotalSessao > 0 && xVistasSessao >= xTotalSessao
          }
        ];
      })
    );
  }, [pvIsQuestaoRevisada, xRevisoesPorQuestao, xSessoesDisponiveis, xQuestoesPorSessao]);

  const xActiveAliasQuestionId = useMemo(() => {
    const xSessaoAtual = xSessoesDisponiveis[activeCategoryId];
    if (!xSessaoAtual) return activeAliasQuestionId;

    const xQuestionIndex = xSessoesQuestoesAtivas.questoes.findIndex(
      xQuestao => xQuestao.id === activeQuestionId
    );
    if (xQuestionIndex === -1) return activeAliasQuestionId;

    return `${xSessaoAtual.sessao}.${xQuestionIndex + 1}`;
  }, [
    activeCategoryId,
    activeQuestionId,
    activeAliasQuestionId,
    xSessoesQuestoesAtivas,
    xSessoesDisponiveis
  ]);

  console.log('xQuestoesSize', xQuestoesSize);
  console.log('xRevisadas', xRevisadas);
  console.log(
    'xQuestoesSize > 0 && xRevisadas >= xQuestoesSize',
    xQuestoesSize > 0 && xRevisadas >= xQuestoesSize
  );

  const xPodeProcessarAvaliacao = xQuestoesSize > 0 && xRevisadas >= xQuestoesSize;

  console.log('xPodeProcessarAvaliacao', xPodeProcessarAvaliacao);

  useEffect(() => {
    if (!id) {
      return;
    }

    let xAtivo = true;

    setXAvaliacao(null);
    xUpsertsPendentesRef.current = 0;
    setXAvaliacaoSalvando(false);
    pvLimparDadosRevisao();
    setXTelaStatus(null);

    const pvCarregarBootstrapRevisao = pAvaliacaoPayload => {
      const pvBloquearTelaSemQuestoes = () => {
        pvLimparDadosRevisao();
        setXTelaStatus({
          title: 'Avaliação sem questões disponíveis',
          description: 'Esta avaliação não possui questões disponíveis para revisão.',
          helperText: 'Revise a configuração do modelo vinculado antes de continuar.'
        });
      };

      setXTelaStatus(null);
      setXAvaliacao(pAvaliacaoPayload);

      services.avaliacoes.listUpload(
        id,
        rUploadsRes => {
          if (!xAtivo) {
            return;
          }

          setXUploadsAvaliacao(Array.isArray(rUploadsRes?.data) ? rUploadsRes.data : []);
        },
        () => {
          if (!xAtivo) {
            return;
          }

          setXUploadsAvaliacao([]);
        }
      );

      const xAvaliacaoModeloId = pAvaliacaoPayload?.avaliacao_modelo_id;
      if (!xAvaliacaoModeloId) {
        pvBloquearTelaSemQuestoes();
        return;
      }

      services.modelosQuestoes.list(
        { avaliacao_modelo_id: xAvaliacaoModeloId },
        rQuestoesRes => {
          if (!xAtivo) {
            return;
          }

          const xQuestoesModelo = Array.isArray(rQuestoesRes?.data) ? rQuestoesRes.data : [];

          if (xQuestoesModelo.length === 0) {
            setXModeloQuestoes([]);
            pvBloquearTelaSemQuestoes();
            return;
          }

          setXModeloQuestoes(xQuestoesModelo);

          services.respostas.list(
            { avaliacao_id: id },
            rRespostasRes => {
              if (!xAtivo) {
                return;
              }

              const xRespostasHydratadasList = hydrateAvaliacaoRespostas(
                Array.isArray(rRespostasRes?.data?.respostas) ? rRespostasRes.data.respostas : []
              );
              const xRespostasPorQuestaoHydratadas =
                xRespostasHydratadasList.respostasPorQuestao || {};

              setXRespostasPorQuestao(xRespostasPorQuestaoHydratadas);
              const xRevisoesHydratadas = pvExtrairRevisoesPorQuestao(
                xRespostasPorQuestaoHydratadas
              );
              xRevisoesPorQuestaoRef.current = xRevisoesHydratadas;
              setXRevisoesPorQuestao(xRevisoesHydratadas);
            },
            () => {
              if (!xAtivo) {
                return;
              }

              setXRespostasPorQuestao({});
              setXRevisoesPorQuestao({});
              xRevisoesPorQuestaoRef.current = {};
            }
          );
        },
        () => {
          if (!xAtivo) {
            return;
          }

          pvLimparDadosRevisao();
        }
      );
    };

    const pvBloquearTelaRevisao = (pAvaliacaoPayload, pTela) => {
      setXAvaliacao(pAvaliacaoPayload);
      pvLimparDadosRevisao();
      setXTelaStatus(pTela);
    };

    services.avaliacoes.read(
      id,
      rRes => {
        if (!xAtivo) {
          return;
        }

        const xAvaliacaoPayload = rRes?.data || null;
        const xStatusAvaliacaoId = getAvaliacaoStatusId(xAvaliacaoPayload?.status);

        if (
          xStatusAvaliacaoId === null ||
          xStatusAvaliacaoId < AVALIACAO_STATUS.RESPOSTA_FINALIZADA.id
        ) {
          pvBloquearTelaRevisao(xAvaliacaoPayload, {
            title: 'Avaliação não está disponível para revisão',
            description: 'Esta avaliação não está disponível para revisão.',
            helperText:
              'A revisão só pode ser iniciada quando a avaliação estiver com status de resposta finalizada.'
          });
          return;
        }

        if (xStatusAvaliacaoId >= AVALIACAO_STATUS.REVISAO_FINALIZADA.id) {
          pvBloquearTelaRevisao(xAvaliacaoPayload, {
            title: 'Questionário Concluído com Sucesso',
            description: 'Agradecemos por sua contribuição para nossa avaliação.'
            // helperText: 'Obrigado por contribuir para a melhoria contínua da nossa instituição.'
          });
          return;
        }

        if (xStatusAvaliacaoId === AVALIACAO_STATUS.RESPOSTA_FINALIZADA.id) {
          services.avaliacoes.revisar(
            { avaliacao_id: xAvaliacaoPayload?.avaliacao_id || id },
            rRevisaoRes => {
              if (!xAtivo) {
                return;
              }

              const xAvaliacaoRevisao = rRevisaoRes?.data
                ? rRevisaoRes.data
                : {
                    ...xAvaliacaoPayload,
                    status: AVALIACAO_STATUS.REVISAO_INICIADA.id
                  };

              pvCarregarBootstrapRevisao(xAvaliacaoRevisao);
            },
            () => {
              if (!xAtivo) {
                return;
              }

              pvBloquearTelaRevisao(xAvaliacaoPayload, {
                title: 'Não foi possível iniciar a revisão',
                description: 'Ocorreu um erro ao iniciar a revisão desta avaliação.',
                helperText: 'Tente novamente em instantes.'
              });
            }
          );
          return;
        }

        pvCarregarBootstrapRevisao(xAvaliacaoPayload);
      },
      () => {
        if (!xAtivo) {
          return;
        }

        setXAvaliacao(null);
        pvLimparDadosRevisao();
        setXTelaStatus(null);
      }
    );

    return () => {
      xAtivo = false;
    };
  }, [id, pvExtrairRevisoesPorQuestao]);

  useEffect(() => {
    const xPrimeiraSessaoDisponivel = Object.keys(xSessoesDisponiveis)[0];
    if (!xPrimeiraSessaoDisponivel) return;

    if (!xSessoesDisponiveisIds.has(activeCategoryId)) {
      actions.handleChangeActiveSessaoId(xPrimeiraSessaoDisponivel);
      const xPrimeiraQuestao = xQuestoesPorSessao[xPrimeiraSessaoDisponivel]?.questoes?.[0];
      if (xPrimeiraQuestao) {
        actions.handleChangeActiveQuestionId(xPrimeiraQuestao.id);
      }
      return;
    }

    const xQuestoesSessaoAtual = xQuestoesPorSessao[activeCategoryId]?.questoes || [];
    const xQuestaoAtivaDisponivel = xQuestoesSessaoAtual.some(
      xQuestao => xQuestao.id === activeQuestionId
    );

    if (!xQuestaoAtivaDisponivel && xQuestoesSessaoAtual[0]) {
      actions.handleChangeActiveQuestionId(xQuestoesSessaoAtual[0].id);
    }
  }, [
    actions,
    activeCategoryId,
    activeQuestionId,
    xSessoesDisponiveis,
    xSessoesDisponiveisIds,
    xQuestoesPorSessao
  ]);

  const handleQuestionChange = useCallback(
    (pQuestionId, pData = {}) => {
      const xRevisaoAtual = xRevisoesPorQuestaoRef.current?.[pQuestionId] || {};
      const xNovaRevisao = {
        ...xRevisaoAtual,
        ...pData
      };
      const xNovoMapaRevisoes = {
        ...xRevisoesPorQuestaoRef.current,
        [pQuestionId]: xNovaRevisao
      };
      const xAvaliacaoId = xAvaliacao?.avaliacao_id;

      xRevisoesPorQuestaoRef.current = xNovoMapaRevisoes;
      setXRevisoesPorQuestao(xNovoMapaRevisoes);

      if (xAvaliacaoId && pQuestionId) {
        pvUpsertRevisaoDebounced(pQuestionId, {
          avaliacao_id: xAvaliacaoId,
          avaliacao_questao_id: Number(pQuestionId),
          revisor_justificativa: xNovaRevisao?.revisor_justificativa || '',
          revisor_nota: xNovaRevisao?.revisor_nota ?? null,
          revisor_usuario_id: xNovaRevisao?.revisor_usuario_id ?? null
        });
      }
    },
    [pvUpsertRevisaoDebounced, xAvaliacao?.avaliacao_id]
  );

  const handleSelectSessao = (_pEvent, pSessao, pIndex) => {
    !validators.isNull(pSessao) && actions.handleChangeActiveSessaoId(pSessao.id);

    const xQuestoes = xQuestoesPorSessao[pSessao.id]?.questoes;
    if (!validators.isEmpty(xQuestoes)) {
      const xFirstSessaoQuestionId = xQuestoes[0].id;
      actions.handleChangeActiveQuestionId(xFirstSessaoQuestionId);
    } else {
      // TODO: Adiconar feedback para o usuário
      console.log('Falha no carregamento das questões');
    }
  };

  const handleProcessarAvaliacao = () => {
    setIsProcessarDialogOpen(true);
  };

  const handleCloseProcessarDialog = () => {
    setIsProcessarDialogOpen(false);
  };

  const handleConfirmProcessarAvaliacao = (pAvaliacaoId, pPodeProcessarAvalicao) => {
    if (!pPodeProcessarAvalicao) return;
    setIsProcessarDialogOpen(true);

    setIsProcessing(true);

    services.avaliacoes.revisarFinalizar(
      { avaliacao_id: pAvaliacaoId },
      rRes => {
        navigate(`/avaliacoes/resultado/${pAvaliacaoId}`);
      },
      rErr => {
        props.responseErrorHandling(rErr);
      },
      () => {
        setIsProcessing(false);
      }
    );
  };

  const pvRenderConteudo = () => {
    if (xTelaStatus) {
      return (
        <AvaliacoesGestorEditStatusView
          title={xTelaStatus.title}
          description={xTelaStatus.description}
          helperText={xTelaStatus.helperText}
        />
      );
    }

    return (
      <>
        <Stack direction="row" gap={6}>
          <Stack
            id="sessoes"
            direction="column"
            width="25%"
            gap={2}
            sx={{
              position: 'sticky',
              top: '-24px',
              alignSelf: 'flex-start',
              maxHeight: 'calc(80vh - 36px)',
              overflowY: 'auto'
            }}>
            <AvaliacoesProgresso
              qtdQuestoes={xQuestoesSize}
              qtdQuestoesRespondiveis={xQuestoesRespondiveisSize}
              respondidas={xRespondidas}
              revisadas={xRevisadas}
            />
            <Divider />
            <SessaoNavProgress
              sessoes={xSessoesProgress}
              activeCategoryId={activeCategoryId}
              onClick={handleSelectSessao}
            />
          </Stack>
          <Stack direction="column" flexGrow="1" minWidth="40%" maxWidth="40%" gap={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="filtro-apenas-sem-visto"
                    size="small"
                    checked={mostrarApenasSemVisto}
                    onChange={pEvent => setMostrarApenasSemVisto(pEvent.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 500 }}>
                    Apenas sem visto
                  </Typography>
                }
              />
              <AvalicoesSaveStatus
                status={xAvaliacao?.status || 0}
                modified={xAvaliacao?.modified || xAvaliacao?.updated_at}
                saving={xAvaliacaoSalvando}
              />
            </Stack>
            <Divider />
            <AvaliacoesEditQuestoes
              categorias={xSessoesDisponiveis}
              activeCategoryId={activeCategoryId}
              activeQuestionId={activeQuestionId}
              onSelect={actions.handleChangeActiveQuestionId}
              questoes={xQuestoesSessaoExibidas}
              questoesReferencia={xQuestoesSessaoAtiva}
              revisoes={xRevisoesPorQuestao}
              respostasGestor={xRespostasPorQuestao}
              revisorUsuarioId={xUsuarioId}
              onQuestionChange={handleQuestionChange}
            />
          </Stack>
          <Stack
            id="sugestoes"
            direction="column"
            maxWidth="35%"
            minWidth="35%"
            sx={{
              position: 'sticky',
              top: '-24px',
              alignSelf: 'flex-start',
              maxHeight: 'calc(80vh - 32px)',
              overflowY: 'auto'
            }}>
            <AvaliacoesEditSugestoes
              activeCategoryId={activeCategoryId}
              activeQuestionId={activeQuestionId}
              activeAliasQuestionId={xActiveAliasQuestionId}
              activeQuestion={xQuestaoAtiva}
              activeResposta={xRespostaQuestaoAtiva}
              uploads={xUploadsAvaliacao}
            />
          </Stack>
        </Stack>

        <Dialog
          open={isProcessarDialogOpen}
          onClose={handleCloseProcessarDialog}
          maxWidth="xs"
          fullWidth>
          <DialogTitle>Processar avaliação?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Revisão concluída? Após confirmar, não será mais possível editar a avaliação.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseProcessarDialog} variant="outlined">
              Ainda não finalizei
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="execute" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
              size="small"
              sx={{ px: 2 }}
              onClick={() => handleConfirmProcessarAvaliacao(id, xPodeProcessarAvaliacao)}
              type="button"
              loading={isProcessing}
              disabled={isProcessing || !xPodeProcessarAvaliacao}
              autoFocus>
              Processar
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  return (
    <PageTemplate
      title={xAvaliacao?.ativo?.ativo_seuid || xAvaliacao?.gestor?.pessoa_fantasia || 'EXEMPLO'}
      subtitle={'Avaliação Gestor'}
      maxWidth={false}
      headerRenderActions={
        xTelaStatus ? null : (
          <Stack direction="row" spacing={2} alignItems="center">
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="execute" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
              size="small"
              sx={{ px: 2, whiteSpace: 'nowrap' }}
              onClick={handleProcessarAvaliacao}
              type="button"
              disabled={!xPodeProcessarAvaliacao}>
              Processar Avaliação
            </LoadingButton>
          </Stack>
        )
      }>
      {pvRenderConteudo()}
    </PageTemplate>
  );
});

AvaliacoesEdit.displayName = 'AvaliacoesEdit';

export default withResponseHandling(AvaliacoesEdit);
